/**
 * Lista que se reordena arrastando (alça à esquerda, @dnd-kit/sortable) ou
 * pelas setas ↑ ↓ — a alternativa do celular e do teclado (spec 4.4).
 * Usada para organizar tarefas e para os passos no editor.
 *
 * `renderItem(item, indice, alca)`: a linha decide onde pôr a alça.
 */
import React from "react";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { FiArrowDown, FiArrowUp, FiMove } from "react-icons/fi";
import styled from "styled-components";

const Alca = styled.button`
  flex: 0 0 auto;
  width: 28px;
  min-height: 32px;
  border: 0;
  background: transparent;
  color: var(--ink-3);
  cursor: grab;
  display: grid;
  place-items: center;
  touch-action: none;

  &:focus-visible {
    outline: 2px solid var(--accent);
    border-radius: 6px;
  }
`;

export const Setas = styled.div`
  flex: 0 0 auto;
  display: flex;
  gap: 4px;

  button {
    width: 30px;
    min-height: 30px;
    border-radius: 7px;
    border: 1px solid var(--border);
    background: var(--surface);
    color: var(--ink-2);
    display: grid;
    place-items: center;
    cursor: pointer;
  }

  button:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }
`;

export function SetasDeOrdem({ indice, total, onMover, rotulo }) {
  return (
    <Setas>
      <button type="button" aria-label={`Subir ${rotulo}`} disabled={indice === 0} onClick={() => onMover(indice, indice - 1)}>
        <FiArrowUp size={15} aria-hidden="true" />
      </button>
      <button
        type="button"
        aria-label={`Descer ${rotulo}`}
        disabled={indice === total - 1}
        onClick={() => onMover(indice, indice + 1)}
      >
        <FiArrowDown size={15} aria-hidden="true" />
      </button>
    </Setas>
  );
}

function ItemOrdenavel({ id, indice, item, renderItem, rotulo }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const estilo = {
    transform: CSS.Transform.toString(transform),
    transition,
    position: "relative",
    zIndex: isDragging ? 2 : undefined,
    boxShadow: isDragging ? "var(--shadow-3)" : undefined,
  };
  const alca = (
    <Alca type="button" aria-label={`Arrastar ${rotulo}`} {...attributes} {...listeners}>
      <FiMove size={16} aria-hidden="true" />
    </Alca>
  );
  return (
    <div ref={setNodeRef} style={estilo}>
      {renderItem(item, indice, alca)}
    </div>
  );
}

export default function ListaOrdenavel({ itens, chave, onMover, renderItem, rotuloDoItem = () => "item" }) {
  const sensores = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const ids = itens.map(chave);

  const aoSoltar = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    onMover(ids.indexOf(active.id), ids.indexOf(over.id));
  };

  return (
    <DndContext sensors={sensores} collisionDetection={closestCenter} onDragEnd={aoSoltar}>
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        {itens.map((item, indice) => (
          <ItemOrdenavel
            key={ids[indice]}
            id={ids[indice]}
            indice={indice}
            item={item}
            renderItem={renderItem}
            rotulo={rotuloDoItem(item, indice)}
          />
        ))}
      </SortableContext>
    </DndContext>
  );
}
